import cx from "classnames";
import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import HandbookButton from "components/HandbookButton.tsx";
import LogoutButton from "components/LogoutButton.tsx";
import Sidebar from "components/Sidebar/Sidebar.tsx";
import KeyboardShortcutsHelp from "pages/metadoc/components/toolbar/KeyboardShortcutsHelp";
import type { TreeNavigationItem } from "components/TreeNavigation/TreeNavigation.tsx";
import TreeNavigation from "components/TreeNavigation/TreeNavigation.tsx";
import {
  SECTION,
  serviceTypeHasCmsSubmission,
} from "constants/SettingsConstants";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useDocumentManagementFileData } from "pages/document-management/hooks/useDocumentManagementFileData";
import { useLienFileData } from "pages/liens/hooks/useLienFileData";
import { buildMetadocPath } from "pages/metadoc/utils/metadocRoutes";
import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toggleAccessPanels } from "reducers/main";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import type { UserType } from "types/DataTypes";
import { getUserAccessConfig } from "utils/permission-utils";
import { useDispatch, useSelector } from "utils/redux";

// nav entries under MSA for the CMS workflow parts; hidden for NTMSA / MSA-111 / Pre-MSA
// (see serviceTypeHasCmsSubmission). Kept in sync with the fm-msa children.
const MSA_CMS_NAV_ITEM_IDS: readonly string[] = [
  "fm-msa-cms-submission",
  "fm-msa-cms",
  "fm-msa-final-settlement",
];

const sidebarNavigation: TreeNavigationItem[] = [
  {
    id: SECTION.TASKS,
    name: "Your tasks",
    to: "/your-tasks",
    children: [],
  },
  {
    id: SECTION.OPERATIONS,
    name: "Operations",
    to: "/operations",
    children: [
      {
        id: "o0-recently-viewed",
        name: "Recent Files",
        to: "/operations/recently-viewed",
        children: [],
      },
      {
        id: "o1",
        name: "Files",
        to: "/operations/files",
        children: [
          {
            id: "o11",
            name: "Claimant Information",
            to: "/operations/files/:fileId/edit/claimant-information",
          },
          {
            id: "o12",
            name: "Service Selection",
            to: "/operations/files/:fileId/edit/service-selection",
          },
          {
            id: "o13",
            name: "Client & Parties",
            to: "/operations/files/:fileId/edit/client-parties",
          },
          {
            id: "o14",
            name: "Linked Claims",
            to: "/operations/files/:fileId/edit/linked-claims",
          },
          {
            id: "o15",
            name: "Audit History",
            to: "/operations/files/:fileId/edit/audit-history",
          },
          {
            id: "o16",
            name: "Notes",
            to: "/operations/files/:fileId/edit/notes",
          },
        ],
      },
      {
        id: "o1-intake",
        name: "Intake",
        to: "/operations/intake",
        children: [],
      },
      {
        id: "o2",
        name: "Clients",
        to: "/operations/clients",
        children: [
          {
            id: "o21",
            name: "Overview",
            to: "/operations/clients/:clientId/overview",
          },
          {
            id: "o21",
            name: "Invoicing Settings",
            to: "/operations/clients/:clientId/invoicing",
          },
          {
            id: "o22",
            name: "Invoicing",
            to: "/operations/clients/:clientId/client-invoicing",
          },
        ],
      },
      {
        id: "o3",
        name: "Claimants",
        to: "/operations/claimants",
        children: [
          {
            id: "o31",
            name: "Overview",
            to: "/operations/claimants/:claimantId/overview",
          },
        ],
      },
      {
        id: "o4",
        name: "Employers",
        to: "/operations/employers",
        children: [
          {
            id: "o41",
            name: "Overview",
            to: "/operations/employers/:employerId/overview",
          },
        ],
      },
      {
        id: "o5",
        name: "Adjusters",
        to: "/operations/adjusters",
        children: [
          {
            id: "o51",
            name: "Overview",
            to: "/operations/adjusters/:adjusterId/overview",
          },
        ],
      },

      {
        id: "o6",
        name: "Insurers",
        to: "/operations/insurers",
        children: [
          {
            id: "o61",
            name: "Overview",
            to: "/operations/insurers/:insurerId/overview",
          },
        ],
      },
      {
        id: "o7",
        name: "TPAs",
        to: "/operations/tpas",
        children: [
          {
            id: "o71",
            name: "Overview",
            to: "/operations/tpas/:tpaId/overview",
          },
        ],
      },
    ],
  },
  {
    id: SECTION.SECTION_111_DASHBOARD,
    name: "Section 111",
    to: "/section-111",
    isHidden: ({ loggedInUser }: { loggedInUser: UserType }) => {
      const userAccess = getUserAccessConfig(loggedInUser);
      const isHidden = !userAccess || !userAccess.can_view_section111;
      return isHidden;
    },
    children: [
      {
        id: "s11-dash",
        name: "Dashboard",
        to: "/section-111/dashboard",
        isHidden: ({
          featureFlags,
        }: {
          featureFlags: Record<string, boolean>;
        }) => {
          return featureFlags
            ? !featureFlags?.[FEATURE_FLAGS.S111_DASHBOARD_ENABLED]
            : false;
        },
        children: [],
      },
      {
        id: "c2",
        name: "Claims",
        to: "/section-111/claims",
        children: [
          {
            id: "cc1",
            name: "Overview",
            to: "/section-111/claims/:claimId/overview",
          },
          {
            id: "cc2",
            name: "MSA",
            to: "/section-111/claims/:claimId/msa",
          },
          {
            id: "cc3",
            name: "ICD Coding",
            to: "/section-111/claims/:claimId/icd-coding",
          },
          {
            id: "cc4",
            name: "Plan Information",
            to: "/section-111/claims/:claimId/plan-information",
          },
          {
            id: "cc5",
            name: "Representative",
            to: "/section-111/claims/:claimId/representative",
          },
          {
            id: "cc6",
            name: "Audit History",
            to: "/section-111/claims/:claimId/audit-history",
          },
          {
            id: "cc7",
            name: "Transmission History",
            to: "/section-111/claims/:claimId/transmission-history",
          },
          {
            id: "cc8",
            name: "Query Response",
            to: "/section-111/claims/:claimId/query-response",
          },
          {
            id: "cc9",
            name: "Additional Claimants",
            to: "/section-111/claims/:claimId/additional-claimants",
          },
          {
            id: "cc10",
            name: "CMP",
            to: "/section-111/claims/:claimId/cmp",
          },
          {
            id: "cc11",
            name: "Notes",
            to: "/section-111/claims/:claimId/notes",
          },
        ],
      },
      {
        id: SECTION.SECTION_111_RRES,
        name: "RREs",
        to: "/section-111/rres",
        children: [
          {
            id: "rr1",
            name: "Overview",
            to: "/section-111/rres/:rreId/overview",
          },
          {
            id: "rr2",
            name: "CMS Submissions",
            to: "/section-111/rres/:rreId/cms-submissions",
          },
          {
            id: "rr3",
            name: "Imports",
            to: "/section-111/rres/:rreId/imports",
          },
          {
            id: "rr4",
            name: "TINS",
            to: "/section-111/rres/:rreId/tins",
          },
          {
            id: "rr5",
            name: "CMS Communication",
            to: "/section-111/rres/:rreId/cms-communication",
          },
          {
            id: "rr6",
            name: "Policies",
            to: "/section-111/rres/:rreId/policies",
          },
          {
            id: "rr7",
            name: "Tags",
            to: "/section-111/rres/:rreId/tags",
          },
        ],
      },
      {
        id: "section-111-reporting",
        name: "Reporting",
        to: "/section-111/reporting",
        children: [],
      },
    ],
  },
  {
    id: SECTION.FUTURE_MEDICAL,
    name: "Future Medical",
    to: "/future-medical",
    isHidden: ({ loggedInUser }: { loggedInUser: UserType }) => {
      return !(
        loggedInUser?.is_superuser ||
        location.pathname.includes("future-medical")
      );
    },
    children: [
      {
        id: "fm-ar-screening",
        name: "AR Screening",
        to: "/future-medical/ar-screening",
        children: [
          {
            id: "fm-ar-ops",
            name: "Operations",
            to: "/future-medical/ar-screening/:reviewId/operations",
          },
          {
            id: "fm-ar-review",
            name: "10/10 Review",
            to: "/future-medical/ar-screening/:reviewId/amended-review",
          },
          {
            id: "fm-ar-analysis",
            name: "Analysis",
            to: "/future-medical/ar-screening/:reviewId/analysis",
          },
          {
            id: "fm-ar-invoice",
            name: "Invoice",
            to: "/future-medical/ar-screening/:reviewId/invoice",
          },
          {
            id: "fm-ar-audit-history",
            name: "Audit History",
            to: "/future-medical/ar-screening/:reviewId/audit-history",
          },
          {
            id: "fm-ar-notes",
            name: "Notes",
            to: "/future-medical/ar-screening/:reviewId/notes",
          },
        ],
      },
      {
        id: "fm-amended-review",
        name: "Amended Review",
        to: "/future-medical/amended-review",
        children: [
          {
            id: "fm-amr-ops",
            name: "Operations",
            to: "/future-medical/amended-review/:msaId/operations",
          },
          {
            id: "fm-amr-query",
            name: "Query",
            to: "/future-medical/amended-review/:msaId/query",
          },
          {
            id: "fm-amr-rated-age",
            name: "Rated Age",
            to: "/future-medical/amended-review/:msaId/rated-age",
          },
          {
            id: "fm-amr-medical",
            name: "Medical Review",
            to: "/future-medical/amended-review/:msaId/medical-review",
          },
          {
            id: "fm-amr-legal",
            name: "Legal Review",
            to: "/future-medical/amended-review/:msaId/legal-review",
          },
          {
            id: "fm-amr-invoice",
            name: "Invoice",
            to: "/future-medical/amended-review/:msaId/invoice",
          },
          {
            id: "fm-amr-client",
            name: "Client Delivery",
            to: "/future-medical/amended-review/:msaId/client-delivery",
          },
          {
            id: "fm-amr-cms-submission",
            name: "CMS Submission",
            to: "/future-medical/amended-review/:msaId/cms-submission",
          },
          {
            id: "fm-amr-cms",
            name: "CMS Status",
            to: "/future-medical/amended-review/:msaId/cms-status",
          },
          {
            id: "fm-final-settlement",
            name: "Final Settlement",
            to: "/future-medical/amended-review/:msaId/fsd",
          },
          {
            id: "fm-amr-audit-history",
            name: "Audit History",
            to: "/future-medical/amended-review/:msaId/audit-history",
          },
          {
            id: "fm-amr-notes",
            name: "Notes",
            to: "/future-medical/amended-review/:msaId/notes",
          },
        ],
      },
      {
        id: "fm-msa",
        name: "MSA",
        to: "/future-medical/msa",
        children: [
          {
            id: "fm-msa-ops",
            name: "Operations",
            to: "/future-medical/msa/:msaId/operations",
          },
          {
            id: "fm-msa-query",
            name: "Query",
            to: "/future-medical/msa/:msaId/query",
          },
          {
            id: "fm-msa-rated-age",
            name: "Rated Age",
            to: "/future-medical/msa/:msaId/rated-age",
          },
          {
            id: "fm-msa-medical",
            name: "Medical Review",
            to: "/future-medical/msa/:msaId/medical-review",
          },
          {
            id: "fm-msa-legal",
            name: "Legal Review",
            to: "/future-medical/msa/:msaId/legal-review",
          },
          {
            id: "fm-msa-invoice",
            name: "Invoice",
            to: "/future-medical/msa/:msaId/invoice",
          },
          {
            id: "fm-msa-client",
            name: "Client Delivery",
            to: "/future-medical/msa/:msaId/client-delivery",
          },
          {
            id: "fm-msa-cms-submission",
            name: "CMS Submission",
            to: "/future-medical/msa/:msaId/cms-submission",
          },
          {
            id: "fm-msa-cms",
            name: "CMS Status",
            to: "/future-medical/msa/:msaId/cms-status",
          },
          {
            id: "fm-msa-final-settlement",
            name: "Final Settlement",
            to: "/future-medical/msa/:msaId/fsd",
          },
          {
            id: "fm-msa-audit-history",
            name: "Audit History",
            to: "/future-medical/msa/:msaId/audit-history",
          },
          {
            id: "fm-msa-notes",
            name: "Notes",
            to: "/future-medical/msa/:msaId/notes",
          },
        ],
      },
      {
        id: SECTION.LCP,
        name: "LCP",
        to: "/future-medical/lcp",
        children: [
          {
            id: "fm31",
            name: "Operations",
            to: "/future-medical/lcp/:lcpId/operations",
          },
          {
            id: "fm32",
            name: "Query",
            to: "/future-medical/lcp/:lcpId/query",
          },
          {
            id: "fm33",
            name: "Rated Age",
            to: "/future-medical/lcp/:lcpId/rated-age",
          },
          {
            id: "fm34",
            name: "Medical Review",
            to: "/future-medical/lcp/:lcpId/medical-review",
          },
          {
            id: "fm35",
            name: "Legal Review",
            to: "/future-medical/lcp/:lcpId/legal-review",
          },
          {
            id: "fm36",
            name: "Invoice",
            to: "/future-medical/lcp/:lcpId/invoice",
          },
          {
            id: "fm37",
            name: "LCP to Client",
            to: "/future-medical/lcp/:lcpId/lcp-to-client",
          },
          {
            id: "fm38",
            name: "Audit History",
            to: "/future-medical/lcp/:lcpId/audit-history",
          },
          {
            id: "fm39",
            name: "Notes",
            to: "/future-medical/lcp/:lcpId/notes",
          },
        ],
      },
      {
        id: SECTION.MCP,
        name: "MCP",
        to: "/future-medical/mcp",
        children: [
          {
            id: "fm41",
            name: "Operations",
            to: "/future-medical/mcp/:mcpId/operations",
          },
          {
            id: "fm42",
            name: "Query",
            to: "/future-medical/mcp/:mcpId/query",
          },
          {
            id: "fm43",
            name: "Rated Age",
            to: "/future-medical/mcp/:mcpId/rated-age",
          },
          {
            id: "fm44",
            name: "Medical Review",
            to: "/future-medical/mcp/:mcpId/medical-review",
          },
          {
            id: "fm45",
            name: "Legal Review",
            to: "/future-medical/mcp/:mcpId/legal-review",
          },
          {
            id: "fm46",
            name: "Invoice",
            to: "/future-medical/mcp/:mcpId/invoice",
          },
          {
            id: "fm47",
            name: "MCP to Client",
            to: "/future-medical/mcp/:mcpId/mcp-to-client",
          },
          {
            id: "fm48",
            name: "Audit History",
            to: "/future-medical/mcp/:mcpId/audit-history",
          },
          {
            id: "fm49",
            name: "Notes",
            to: "/future-medical/mcp/:mcpId/notes",
          },
        ],
      },
    ],
  },
  {
    id: SECTION.LIENS,
    name: "Liens",
    to: "/liens", // this is overridden with workspaceUrl: below
    isHidden: ({ location }) => {
      return !location.pathname.includes("liens");
    },
    children: [],
  },
  {
    id: SECTION.DOCUMENT_MANAGEMENT,
    name: "Document Management",
    to: "/document-management",
    isHidden: ({ location }) => {
      return !location.pathname.includes("document-management");
    },
    children: [
      {
        id: "dm-operations",
        name: "Operations",
        to: "/document-management/:documentManagementFileId/operations",
      },
      {
        id: "dm-invoice",
        name: "Invoice",
        to: "/document-management/:documentManagementFileId/invoice",
      },
      {
        id: "dm-notes",
        name: "Notes",
        to: "/document-management/:documentManagementFileId/notes",
      },
    ],
  },
  {
    id: SECTION.JOBS,
    name: "Jobs",
    to: "/jobs",
    isHidden: ({ loggedInUser }: { loggedInUser: UserType }) => {
      return !loggedInUser?.is_staff;
    },
    children: [],
  },
  {
    id: SECTION.FINANCE,
    name: "Reporting",
    to: "/reporting",
    isHidden: ({ loggedInUser }: { loggedInUser: UserType }) => {
      return !(
        loggedInUser?.enabled_reports && loggedInUser.enabled_reports.length > 0
      );
    },
    children: [
      {
        id: "finance",
        name: "Finance",
        to: "/reporting/finance",
      },
      {
        id: "operations",
        name: "Operations",
        to: "/reporting/operations",
      },
    ],
  },
  {
    id: SECTION.SETTINGS,
    name: "Settings",
    to: "/settings",
    isHidden: ({ loggedInUser }: { loggedInUser: UserType }) => {
      return !loggedInUser?.has_management_authority;
    },
    children: [],
  },
  {
    id: SECTION.REFERRAL_SETTINGS,
    name: "Referral Settings",
    to: "/referral-settings",
    isHidden: ({ loggedInUser }: { loggedInUser: UserType }) => {
      return !(
        // only superusers and users with intake approval authority can see this
        (
          loggedInUser?.is_superuser ||
          loggedInUser?.has_intake_approval_authority
        )
      );
    },
    children: [],
  },
];

type KaboodleLeftSideBarPropTypes = {
  isVisible?: boolean;
};

const KaboodleLeftNavigationPanel = ({
  isVisible,
}: KaboodleLeftSideBarPropTypes) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const constants = useSelector((state) => state.shared.constants);
  const { data: organizationSettings } = useGetOrganizationSettingsQuery();

  // getService from future-medical reducer
  const { currentService } = useSelector((state) => state.futureMedical);

  // get workspaceUrl from lien file data hook, this is so we can click top parent Lien and be taken to specific workspace url
  const { workspaceUrl: liensWorkspaceUrl, lienFileData } = useLienFileData();

  const {
    workspaceUrl: documentManagementWorkspaceUrl,
    fileId: documentManagementFileId,
  } = useDocumentManagementFileData();

  const sideNavigationActive = useSelector(
    (state) => state.main.sideNavigationActive,
  );

  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const isMetadocEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_ENABLED];
  const currentFutureMedicalSection = location.pathname.split("/")[2];
  // only trust currentService for the MSA we're actually looking at; it can
  // lag behind the URL while a newly opened MSA is still loading
  const currentMsaHasCmsSubmission =
    !params.msaId ||
    String(currentService?.id) !== params.msaId ||
    serviceTypeHasCmsSubmission(currentService?.type);
  const futureMedicalMetaDocumentsItem = useMemo(() => {
    if (
      !isMetadocEnabled ||
      !currentService?.file?.id ||
      !currentFutureMedicalSection
    ) {
      return null;
    }

    return {
      id: "fm-meta-documents",
      name: "Meta Documents",
      to: buildMetadocPath({
        workspace: "future-medical",
        serviceSection: currentFutureMedicalSection as
          | "msa"
          | "amended-review"
          | "mcp"
          | "lcp"
          | "ar-screening",
        serviceId: currentService.id,
        fileId: currentService.file.id,
      }),
      isSeparated: true,
    };
  }, [
    isMetadocEnabled,
    currentFutureMedicalSection,
    currentService?.id,
    currentService?.file?.id,
  ]);
  const documentManagementMetaDocumentsItem = useMemo(() => {
    if (!isMetadocEnabled || !params.documentManagementFileId) {
      return null;
    }

    return {
      id: "dm-meta-documents",
      name: "Meta Documents",
      to: buildMetadocPath({
        workspace: "document-management",
        documentManagementFileId: params.documentManagementFileId,
      }),
      isSeparated: true,
    };
  }, [isMetadocEnabled, params.documentManagementFileId]);
  const futureMedicalNavigationChildren = useMemo(() => {
    const futureMedicalItem = sidebarNavigation.find(
      (item) => item.id === SECTION.FUTURE_MEDICAL,
    );

    if (!futureMedicalItem?.children) {
      return null;
    }

    return futureMedicalItem.children.map((child) => {
      let nestedChildren = child.children;

      // NTMSA / MSA-111 / Pre-MSA Snapshot are never submitted to CMS: drop the CMS Submission,
      // CMS Status and Final Settlement entries for them
      if (child.id === "fm-msa" && !currentMsaHasCmsSubmission) {
        nestedChildren = nestedChildren?.filter(
          (nestedChild) => !MSA_CMS_NAV_ITEM_IDS.includes(nestedChild.id),
        );
      }

      if (
        futureMedicalMetaDocumentsItem &&
        child.to === `/future-medical/${currentFutureMedicalSection}`
      ) {
        nestedChildren = [
          ...(nestedChildren?.filter(
            (nestedChild) => nestedChild.id !== "fm-meta-documents",
          ) ?? []),
          futureMedicalMetaDocumentsItem,
        ];
      }

      return nestedChildren === child.children
        ? child
        : { ...child, children: nestedChildren };
    });
  }, [
    currentFutureMedicalSection,
    futureMedicalMetaDocumentsItem,
    currentMsaHasCmsSubmission,
  ]);

  const leftNavItems = useMemo(() => {
    const navigationItems = sidebarNavigation.map((item) => ({
      ...item,
      children:
        item.id === SECTION.FUTURE_MEDICAL && futureMedicalNavigationChildren
          ? futureMedicalNavigationChildren
          : item.children
            ? [...item.children]
            : [],
    }));

    return navigationItems.filter((item) => {
      // if in future medical section, we override item.to with the overview page
      if (item.id === SECTION.FUTURE_MEDICAL && currentService?.overview_url) {
        item.to = currentService.overview_url;
      }

      // if in liens section and we have a workspace url, use it
      if (item.id === SECTION.LIENS && liensWorkspaceUrl) {
        item.to = liensWorkspaceUrl;
      }

      if (
        item.id === SECTION.DOCUMENT_MANAGEMENT &&
        documentManagementWorkspaceUrl
      ) {
        item.to = documentManagementWorkspaceUrl;
        item.children = [
          ...(item.children?.filter(
            (child) => child.id !== "dm-meta-documents",
          ) ?? []),
          ...(documentManagementMetaDocumentsItem
            ? [documentManagementMetaDocumentsItem]
            : []),
        ];
        item.children.forEach((child) => {
          if (
            child.id !== "dm-meta-documents" &&
            !child.to.includes("?file_id=")
          ) {
            child.to += `?file_id=${documentManagementFileId}`;
          }
        });
      }

      // filter out invoice navigation item from Document Management if QuickBooks integration is not enabled
      if (item.id === SECTION.DOCUMENT_MANAGEMENT) {
        item.children = item.children.filter((child) => {
          // hide invoice navigation item if quickbooks integration is not enabled
          if (child.id === "dm-invoice" && child.name === "Invoice") {
            return organizationSettings?.quickbooks_integration_enabled;
          }
          return true;
        });
      }

      // inject Meta Documents into Operations > Files nav after Notes
      if (item.id === SECTION.OPERATIONS && isMetadocEnabled) {
        const filesItem = item.children.find((child) => child.id === "o1");
        if (filesItem) {
          // remove existing meta documents item if it exists (to update with current fileId)
          filesItem.children = filesItem.children?.filter(
            (c) => c.id !== "ops-meta-documents",
          );

          const fileId = params.fileId;
          filesItem.children?.push({
            id: "ops-meta-documents",
            name: "Meta Documents",
            to: fileId
              ? buildMetadocPath({
                  workspace: "operations",
                  fileId,
                })
              : "/operations/files/:fileId/metadoc",
            isSeparated: true,
          });
        }
      }

      // for user who dont have operations authority, only show Files in the left nav
      if (item.id === SECTION.OPERATIONS) {
        if (!loggedInUser?.has_operations_authority) {
          item.children = item.children.filter((child) => child.id === "o1");
        }

        // filter out invoicing navigation items if QuickBooks integration is not enabled
        item.children = item.children.map((child) => {
          if (child.id === "o2") {
            // Clients section
            const filteredClientChildren = child.children?.filter(
              (clientChild) => {
                // hide invoicing navigation items if quickbooks integration is not enabled
                if (
                  clientChild.id === "o21" &&
                  clientChild.name === "Invoicing"
                ) {
                  return organizationSettings?.quickbooks_integration_enabled;
                }
                if (
                  clientChild.id === "o22" &&
                  clientChild.name === "Invoicing"
                ) {
                  return organizationSettings?.quickbooks_integration_enabled;
                }
                return true;
              },
            );
            return { ...child, children: filteredClientChildren };
          }
          return child;
        });
      }

      return !item?.isHidden?.({
        loggedInUser,
        location,
        featureFlags: constants?.FEATURE_FLAGS,
      })
        ? item
        : null;
    });
  }, [
    loggedInUser,
    location,
    liensWorkspaceUrl,
    organizationSettings,
    constants,
    lienFileData,
    params.fileId,
    params.documentManagementFileId,
    documentManagementWorkspaceUrl,
    documentManagementFileId,
    isMetadocEnabled,
    documentManagementMetaDocumentsItem,
    futureMedicalNavigationChildren,
  ]);

  const userAccess = getUserAccessConfig(loggedInUser);
  const isMetaDocRoute = /(^|\/)metadoc(\/|$)/.test(location.pathname);

  // this !loggedInUser fixes a bug where two sections stay open. some
  // weird re-render happens when isHidden() is called causing this,
  // but this fixes it for now.
  return !loggedInUser ? null : (
    <>
      <Sidebar position={"left"} isvisible={isVisible}>
        <div
          className={cx("sidebar-upper-items", {
            active: sideNavigationActive,
          })}
        >
          <span>
            <ButtonIcon
              onClick={() => {
                dispatch(toggleAccessPanels());
              }}
            >
              {sideNavigationActive ? "chevron_right" : "chevron_left"}
            </ButtonIcon>
          </span>

          <div className="nav-icons">
            <span>
              <span>
                <ButtonIcon
                  onClick={() => {
                    navigate("/your-tasks");
                  }}
                >
                  receipt_long
                </ButtonIcon>
              </span>
              {userAccess.can_view_section111 ? (
                <span>
                  <ButtonIcon
                    iconType="text"
                    onClick={() => {
                      navigate("/section-111");
                    }}
                  >
                    Section 111
                  </ButtonIcon>
                </span>
              ) : null}
              {loggedInUser.is_staff && (
                <span>
                  <ButtonIcon
                    iconType="text"
                    onClick={() => {
                      navigate("/jobs");
                    }}
                  >
                    Jobs
                  </ButtonIcon>
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="sidebar-lower-items">
          {isMetaDocRoute && (
            <span>
              <KeyboardShortcutsHelp iconOnly />
            </span>
          )}
          <span>
            <HandbookButton />
          </span>
          <span>
            <LogoutButton />
          </span>
        </div>
      </Sidebar>

      <section className="left-sidebar">
        <TreeNavigation data={leftNavItems} />
      </section>
    </>
  );
};

export default KaboodleLeftNavigationPanel;
